import { useState } from 'react';
import { HoldingsRevenue, HoldingsCompany, HoldingsRevenuePeriodType } from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface RevenueFormProps {
  revenue?: HoldingsRevenue | null;
  companies: HoldingsCompany[];
  onClose: () => void;
  onSaved: () => void;
}

export default function RevenueForm({ revenue, companies, onClose, onSaved }: RevenueFormProps) {
  const isEditing = !!revenue;

  const [companyId, setCompanyId] = useState(revenue?.companyId || '');
  const [periodType, setPeriodType] = useState<HoldingsRevenuePeriodType>(revenue?.periodType || 'month');
  const [periodStart, setPeriodStart] = useState(revenue?.periodStart || '');
  const [revenueDollars, setRevenueDollars] = useState(
    revenue?.revenueCents ? (revenue.revenueCents / 100).toString() : ''
  );
  const [notes, setNotes] = useState(revenue?.notes || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  // Calculate period end based on type and start
  const calculatePeriodEnd = (start: string, type: HoldingsRevenuePeriodType): string => {
    if (!start) return '';
    const startDate = new Date(start);
    if (type === 'month') {
      // End of month
      const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      return endDate.toISOString().split('T')[0];
    } else {
      // End of quarter (3 months)
      const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);
      return endDate.toISOString().split('T')[0];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId) {
      setError('Please select a company');
      return;
    }

    if (!periodStart) {
      setError('Please select a period start date');
      return;
    }

    const revenueAmount = parseFloat(revenueDollars);
    if (isNaN(revenueAmount) || revenueAmount < 0) {
      setError('Please enter a valid revenue amount');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const periodEnd = calculatePeriodEnd(periodStart, periodType);

      const payload = {
        companyId,
        periodType,
        periodStart,
        periodEnd,
        revenueCents: Math.round(revenueAmount * 100),
        notes: notes.trim() || null,
        source: 'manual'
      };

      const url = isEditing
        ? `${API_URL}/holdings/revenue/${revenue.id}`
        : `${API_URL}/holdings/revenue`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save revenue');
      }
    } catch (e) {
      setError('Failed to save revenue');
    } finally {
      setSaving(false);
    }
  };

  // Generate month options for quick selection
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    return options;
  };

  // Generate quarter options
  const getQuarterOptions = () => {
    const options = [];
    const now = new Date();
    const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
    const currentYear = now.getFullYear();

    for (let i = 0; i < 12; i++) {
      let quarter = currentQuarter - i;
      let year = currentYear;
      while (quarter <= 0) {
        quarter += 4;
        year -= 1;
      }
      const month = (quarter - 1) * 3; // 0, 3, 6, or 9
      const date = new Date(year, month, 1);
      options.push({
        value: date.toISOString().split('T')[0],
        label: `Q${quarter} ${year}`
      });
    }
    return options;
  };

  const periodOptions = periodType === 'month' ? getMonthOptions() : getQuarterOptions();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Revenue' : 'Add Revenue'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Company *
            </label>
            <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">Select company...</option>
              {companies
                .filter(c => c.status === 'active')
                .map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
            </Select>
          </div>

          {/* Period Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Period Type
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPeriodType('month')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  periodType === 'month'
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setPeriodType('quarter')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  periodType === 'quarter'
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                Quarterly
              </button>
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Period *
            </label>
            <Select value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}>
              <option value="">Select period...</option>
              {periodOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </div>

          {/* Revenue Amount */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Revenue Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={revenueDollars}
                onChange={(e) => setRevenueDollars(e.target.value)}
                placeholder="0.00"
                className="pl-7"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Revenue'} />
          </div>
        </form>
      </div>
    </div>
  );
}
